/**
 * Pied-à-Terre surcharge (NYS Tax Law §§1350–1356), in effect 7/1/2026 – 6/30/2031.
 * Rules and rate schedule: https://pat.marketproof.com/pied-a-terre-tax-law
 *
 * The statute keys off the DOF-calculated market value, which lives in
 * `supplemental_tax_roll.fmv` (taxyr 2027 — the only roll loaded). That is a DOF
 * valuation, NOT an appraised or comparable-sale value: class 2 condo/co-op lots are
 * valued by the income approach and run far below what the unit would trade for.
 *
 * Two kinds of row, distinguished by `rectype`:
 *   - `rectype:1` — one per tax lot. Condos and 1-3 family homes bill per lot, so the
 *     row IS the property; a co-op bills at the corporation lot, so its row carries the
 *     whole building's value.
 *   - `rectype:U` — DOF's per-unit valuation inside a lot, keyed by `bbl` + `aptno`
 *     (~36k rows, mostly co-op). When one exists for the unit it is authoritative;
 *     otherwise a co-op unit falls back to pro-rating the lot row — see _prorateCoopFmv.
 *
 * Look the row up by unit key first and by BBL second. Neither alone is enough: the roll
 * keys off DOF's primary address, which often differs from ours (111 W 57th St is
 * `p-1-106-w-58th-st-...`, 1 Fifth Ave is `p-1-68-washington-mews-...`), while a BBL query
 * returns every unit in the building, so on a large co-op the unit's own row can fall
 * outside the row window. Co-op unit records carry the corporation's block/lot, so BBL
 * still reaches the right lot for them when the key misses.
 *
 * Phase 1 rates (through 6/30/2028) apply to the FULL market value once a property
 * crosses its threshold, not to the excess over it.
 *
 * The same thresholds drive the `pat_value` filter on the Owner Outreach, Inventory and
 * Houses searches (`_patFmvQuery` in helpers/filters.js); keep the two in sync.
 */
import { _isCCC, _zeroPad } from './utils';

export const PAT_LAW_URL = 'https://pat.marketproof.com/pied-a-terre-tax-law';
export const PAT_CHECK_URL = 'https://pat.marketproof.com/check';
// Portfolio Exposure Analysis — screens every property billed to a firm, ranks them by estimated
// surcharge and monitors DOF determinations. The single-property check is what this panel already
// does, so the CTA sells the thing the page cannot do for itself.
export const PAT_REPORT_URL = 'https://pat.marketproof.com/#report';

/**
 * Deep link into the public PAT checker, prefilled for this property.
 *
 * The checker resolves on address + borough alone (verified: /check?address=3180+tierney+pl&boro=2
 * returns the lot, its DOF value and the same surcharge we compute). `type` and `unit` only sharpen
 * the match, so both are omitted when we cannot state them confidently — a wrong unit would resolve
 * to a different apartment. Houses send no type: the checker's third option is a 1-3 family label
 * whose parameter value we have not confirmed.
 */
export const _patCheckUrl = (property) => {
	if (!property) return PAT_CHECK_URL;
	const address = property.display_full_street_address || property.full_street_address;
	if (!address) return PAT_CHECK_URL;

	const params = ['address=' + encodeURIComponent(String(address).toLowerCase())];
	if (property.borough_code > 0) params.push('boro=' + property.borough_code);

	const ownershipType = property.ownership_type ? String(property.ownership_type).toLowerCase().replace(/[^a-z]/g, '') : '';
	if (ownershipType === 'condo') params.push('type=condo');
	else if (ownershipType === 'coop' || ownershipType === 'condop') params.push('type=coop');

	const unit = String(property.address_2_normalized || '').replace(/^\s*(apt|apartment|unit)\s*/i, '').trim();
	if (unit) params.push('unit=' + encodeURIComponent(unit));

	return `${PAT_CHECK_URL}?${params.join('&')}`;
};
export const PAT_EFFECTIVE_START = 'July 1, 2026';
export const PAT_EFFECTIVE_END = 'June 30, 2031';
export const PAT_PHASE_1_END = 'June 30, 2028';
export const PAT_APPLICATION_DEADLINE = 'September 18, 2026';

/**
 * None of these are visible in DOF data, which is why crossing the value threshold only
 * makes a property a candidate. The owner has to file with DOF to claim one.
 */
export const PAT_EXEMPTIONS = [
	'It is the primary residence of the owner or an immediate family member',
	'It is leased to a tenant under a bona-fide arm\'s-length lease of a year or more',
	'It has no certificate of occupancy',
	'It is a new-development unit still held by the sponsor',
];

export const patTiers = {
	ccc: {
		key: 'ccc',
		label: 'Condo / Co-op',
		threshold: 1000000,
		brackets: [
			{ min: 1000000, max: 3000000, rate: 0.04 },
			{ min: 3000000, max: 5000000, rate: 0.0525 },
			{ min: 5000000, max: null, rate: 0.065 },
		],
	},
	home: {
		key: 'home',
		label: '1–3 Family Home',
		threshold: 5000000,
		brackets: [
			{ min: 5000000, max: 15000000, rate: 0.008 },
			{ min: 15000000, max: 25000000, rate: 0.0105 },
			{ min: 25000000, max: null, rate: 0.013 },
		],
	},
};

/**
 * DOF building classes whose row covers a whole building rather than one apartment, so
 * the value has to be pro-rated: the co-op corporation lots (plain D1-D9 are rental
 * elevator buildings, not co-ops) plus R9, a co-op section inside a condominium.
 */
export const coopLotClasses = ['D0', 'D4', 'C6', 'C8', 'R9'];

/**
 * Which rate schedule a property falls under, or false when the statute does not
 * cover the property class at all (rentals, 4+ unit multi-family, commercial).
 *
 * DOF's tax class decides first: class 1 takes the $5M schedule whatever we call the
 * property, so a condo billed in class 1 (R3/R6 in 1A/1C) is NOT on the $1M condo
 * ladder. That is how Marketproof's published citywide counts split, and it keeps this
 * verdict in step with the `pat_value` search filter. ownership_type and the roll's
 * building class fill in behind it, for records with no class on file.
 */
export const _getPiedATerreTier = (property, roll) => {
	const taxClass = roll && roll.tax_class ? String(roll.tax_class).toUpperCase() : '';
	const bldgClass = roll && roll.bldg_class
		? String(roll.bldg_class).toUpperCase()
		: (property && property.building_class ? String(property.building_class).toUpperCase() : '');
	const ownershipType = property && property.ownership_type ? String(property.ownership_type).toLowerCase() : '';

	// R* = condo lot, minus the commercial condo classes; coopLotClasses = co-op lots.
	const isCCCLot = bldgClass.charAt(0) === 'R'
		? ['R5', 'R7', 'R8'].indexOf(bldgClass) < 0
		: coopLotClasses.indexOf(bldgClass) >= 0;

	if(taxClass.charAt(0) === '1') return patTiers.home;
	if(_isCCC(property)) return patTiers.ccc;
	if(/single|multi|two.?family|three.?family|four.?family|five.?family/.test(ownershipType)) return patTiers.home;
	if(isCCCLot && taxClass.charAt(0) !== '4') return patTiers.ccc;
	if(!taxClass && (bldgClass.charAt(0) === 'A' || bldgClass.charAt(0) === 'B' || bldgClass === 'C0')) return patTiers.home;
	return false;
};

/** The bracket a DOF market value lands in, or false when it is under the threshold. */
export const _getPiedATerreBracket = (fmv, tier) => {
	if(!tier || !(fmv > 0)) return false;
	const bracket = tier.brackets.filter(b => fmv > b.min && (b.max === null || fmv <= b.max))[0];
	// The bottom bracket is inclusive of the threshold itself ($1M condo / $5M home).
	if(!bracket && fmv === tier.threshold) return tier.brackets[0];
	return bracket || false;
};

/**
 * Split a co-op corporation's lot value down to a single unit. DOF assesses the
 * building, so this is an estimate: the unit's share of the corporation (preferred
 * when known) or an even split across residential units.
 */
export const _prorateCoopFmv = (roll, property) => {
	if(!roll || !(roll.fmv > 0)) return false;
	const shares = property && property.residential_shares > 0 ? property.residential_shares : false;
	if(shares && shares <= 100) return { fmv: Math.round(roll.fmv * (shares / 100)), basis: 'shares', shares };
	const units = roll.dof_residential_units > 0
		? roll.dof_residential_units
		: (property && property.total_units > 0 ? property.total_units : false);
	if(units) return { fmv: Math.round(roll.fmv / units), basis: 'units', units };
	return false;
};

/**
 * Does the property meet the surcharge's VALUE threshold, and what would the surcharge
 * cost if it does. Meeting the threshold is not the same as owing the tax: the statute
 * only reaches non-primary residences, and the exemptions in PAT_EXEMPTIONS are not
 * knowable from DOF data, so nothing here should be phrased as "subject" or "qualifies".
 *
 * @param property  we3-properties doc (needs ownership_type; residential_shares helps for co-ops)
 * @param roll      supplemental_tax_roll doc, or false when the lot has no row
 * @param isCoopUnit true when `roll` is the co-op corporation's lot rather than the unit itself
 * @returns false when there is nothing to say (no roll row), otherwise the verdict
 */
export const _getPiedATerreStatus = (property, roll, isCoopUnit) => {
	const tier = _getPiedATerreTier(property, roll);

	if(!tier){
		return {
			tier: false,
			meetsThreshold: false,
			covered: false,
			fmv: roll && roll.fmv > 0 ? roll.fmv : false,
			taxYear: roll && roll.taxyr ? roll.taxyr : false,
			note: 'The surcharge covers condos, co-ops and 1–3 family homes. This property class is outside it.',
		};
	}

	if(!roll || !(roll.fmv > 0)) return false;

	let fmv = roll.fmv;
	let estimated = false;
	let estimateBasis = false;
	if(isCoopUnit){
		const prorated = _prorateCoopFmv(roll, property);
		if(!prorated) return false;
		fmv = prorated.fmv;
		estimated = true;
		estimateBasis = prorated;
	}

	// The home tier is the statute's 1-3 family scope, which is DOF tax class 1. A
	// multi-family sitting in class 2 has 4+ units and is outside it, so flag the
	// verdict rather than asserting it — Owner Outreach's filter, which only sees
	// ownership_type through the join, would still return the property.
	const taxClass = roll.tax_class ? String(roll.tax_class).toUpperCase() : '';
	const classCaveat = tier.key === 'home' && !!taxClass && taxClass.charAt(0) !== '1';

	const bracket = _getPiedATerreBracket(fmv, tier);
	const meetsThreshold = !!bracket;

	return {
		tier,
		covered: true,
		meetsThreshold,
		fmv,
		taxYear: roll.taxyr || false,
		taxClass: roll.tax_class || false,
		bldgClass: roll.bldg_class || false,
		bbl: roll.bbl || false,
		threshold: tier.threshold,
		bracket: bracket || false,
		rate: bracket ? bracket.rate : 0,
		amount: bracket ? Math.round(fmv * bracket.rate) : 0,
		shortfall: meetsThreshold ? 0 : tier.threshold - fmv,
		estimated,
		estimateBasis,
		buildingFmv: estimated ? roll.fmv : false,
		// DOF valued this apartment on its own (rectype U) rather than us deriving it.
		unitLevel: String(roll.rectype || '').toUpperCase() === 'U',
		classCaveat,
	};
};

/**
 * "Apt 12C" / "12-c" / "#12C" all collapse to 12C so DOF's `aptno` can be matched.
 * Deliberately exact after normalising — DOF labels combined and floor-style units
 * differently from us ("1516B", "3" vs "apt 3fl"), and a fuzzy match there would
 * attach one owner's value to another owner's apartment.
 */
export const _normalizeAptNo = value => String(value || '')
	.toUpperCase()
	.replace(/\b(APT|APARTMENT|UNIT)\b/g, '')
	.replace(/[^A-Z0-9]/g, '');

const ROLL_FL = 'bbl,parid,rectype,aptno,taxyr,tax_class,bldg_class,fmv,dof_property,dof_building,dof_residential_units';

// rows has to clear the biggest co-op: one BBL holds a row per valued unit (1100 Park Ave has 72),
// and a short window silently drops the unit's own row, leaving the building-wide lot row to be
// pro-rated in its place — which reads as "below threshold" for a unit that is over it.
const rollQuery = (apiURL, fq) => ({
	url: apiURL + 'supplemental_tax_roll?cache=120',
	method: 'post',
	query: { q: '*:*', fq, fl: ROLL_FL, sort: 'taxyr desc', rows: 500, wt: 'json' },
});

/**
 * Pick the right row out of everything filed under one BBL.
 *
 * A DOF per-unit (`rectype:U`) row for this apartment wins — that is DOF's own valuation
 * of the unit and needs no estimating. Those rows now carry a unit-level `dof_property`,
 * so match on the key first and fall back to the apartment number for rows still keyed
 * to the building. Failing both, take the lot row, flagged for pro-rating when it covers
 * a whole co-op corporation.
 */
const pickRollRow = (docs, property) => {
	const rows = (docs || []).filter(d => d && d.fmv > 0);
	if(!rows.length) return { roll: false, isCoopUnit: false };

	const isUnitRow = d => String(d.rectype).toUpperCase() === 'U';
	const apt = _normalizeAptNo(property.address_2_normalized);

	// Apartment number first, key second. The keys on U rows are not always right — 1100 Park Ave
	// files apt 10B, 11B and 1B all under `...-apt-1b-...` (leading digit dropped), so trusting the
	// key alone would hand one unit's valuation to another. `aptno` is DOF's own label and matches
	// what we show as the unit number.
	if(apt){
		const unitRow = rows.filter(d => isUnitRow(d) && _normalizeAptNo(d.aptno) === apt)[0];
		if(unitRow) return { roll: unitRow, isCoopUnit: false };
	}

	const keys = [property.key].concat(Array.isArray(property.alternate_keys) ? property.alternate_keys : []);
	const keyed = rows.filter(d => isUnitRow(d) && keys.indexOf(d.dof_property) >= 0 && (!apt || !d.aptno))[0];
	if(keyed) return { roll: keyed, isCoopUnit: false };

	const lotRow = rows.filter(d => String(d.rectype).toUpperCase() !== 'U')[0];
	if(!lotRow) return { roll: false, isCoopUnit: false };
	return {
		roll: lotRow,
		isCoopUnit: coopLotClasses.indexOf(String(lotRow.bldg_class || '').toUpperCase()) >= 0
			&& lotRow.dof_residential_units > 1,
	};
};

/**
 * Pull the tax roll row for a property: by unit key first, then by BBL, then (co-ops only)
 * by the building's address aliases.
 *
 * @param axiosCall  _axiosCall from helpers/apicalls (injected to keep this file free of API wiring)
 * @returns {roll, isCoopUnit} — isCoopUnit means the value covers the whole co-op and
 *          has to be pro-rated down to the unit
 */
export const _fetchPiedATerreRoll = (axiosCall, apiURL, property) => {
	const empty = { roll: false, isCoopUnit: false };
	if(!property || !property.key) return Promise.resolve(empty);

	const hasBBL = property.borough_code > 0 && property.block > 0 && property.lot > 0;
	const bbl = hasBBL ? `${property.borough_code}${_zeroPad(property.block, 5)}${_zeroPad(property.lot, 4)}` : false;

	const keys = [property.key];
	if(Array.isArray(property.alternate_keys)) property.alternate_keys.forEach(k => keys.push(k));
	// Condops are co-op corporations for tax purposes, so they bill at the building lot too.
	const ownershipType = property.ownership_type ? String(property.ownership_type).toLowerCase().replace(/[^a-z]/g, '') : '';
	const billsAtBuildingLot = ownershipType === 'coop' || ownershipType === 'condop';
	if(billsAtBuildingLot && property.building) keys.push(property.building.replace(/^b-/, 'p-'));

	// Last resort, co-ops only: the whole corporation is one lot, so matching the roll's
	// address aliases for the building still lands on the right row. Not safe for condos,
	// where the building's rows are a pile of other people's units.
	const byBuilding = () => {
		if(!billsAtBuildingLot || !property.building) return empty;
		return axiosCall(rollQuery(apiURL, [`dof_alternate_buildings:"${property.building}"`]))
			.then(res => pickRollRow(res && res.docs, property));
	};

	// dof_alternate_properties carries every address variant of the lot, dof_property only
	// DOF's primary one — condo lots populate both, houses only the latter.
	const keyFq = ['dof_property', 'dof_alternate_properties']
		.map(field => `{!terms f=${field}}${keys.join(',')}`)
		.map(clause => `_query_:"${clause.replace(/"/g, '\\"')}"`)
		.join(' OR ');

	const byBBL = () => {
		if(!bbl) return empty;
		return axiosCall(rollQuery(apiURL, [`bbl:${bbl}`]))
			.then(res => {
				const picked = pickRollRow(res && res.docs, property);
				return picked.roll ? picked : byBuilding();
			});
	};

	// Key first, BBL second. Both find the same row for most properties, but the key query returns
	// only this unit's rows while a BBL query returns every unit in the building — so on a large
	// co-op the key path can't lose the unit's own row behind a row limit.
	return axiosCall(rollQuery(apiURL, [keyFq]))
		.then(res => {
			const picked = pickRollRow(res && res.docs, property);
			return picked.roll ? picked : byBBL();
		})
		.catch(() => empty);
};
